/**
 * OIDC / PKCE against Keycloak (WP-03.1, decision D-12). The SPA is a public client (`onesphere-web`); tokens live
 * in sessionStorage for the tab and are renewed with the refresh token by oidc-client-ts. The access token is
 * attached to every Frappe call (see frappe.ts); Frappe validates it through the `onesphere.auth` hook and maps
 * the `os_*` realm roles to OneSphere roles, so the same token authorises both the UI and the API.
 */
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { OF_ROLE_LABEL_KEYS, isOneFleetRole } from "./onefleet/roles";

export const KEYCLOAK_URL = (import.meta.env.VITE_KEYCLOAK_URL ?? "http://localhost:51081").replace(/\/$/, "");
export const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? "onesphere";
export const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "onesphere-web";
export const AUTHORITY = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`;

export type Identity = {
  /** Keycloak subject (stable id). */
  sub: string;
  username: string;
  name: string;
  email?: string;
  /** Realm roles starting with os_ (e.g. os_executive) or of_ (OneFleet add-on, AD-03). */
  roles: string[];
  expiresAt: number;
};

/** Decode a JWT payload without verifying it — verification is the server's job (Frappe auth hook). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) throw new Error("not a JWT");
  const base64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
  const binary: string = atob(base64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder("utf-8").decode(bytes));
}

/** The super administrators: the realm's `os_admin` (the Administrators group — `admin`, `sivakumar`).
 *
 * They are no longer an exception for ERPNext: **nothing in this application links to it** (owner's rule, 2026-09-28,
 * superseding the super-administrator-only links of 2026-09-21). An administrator who needs the desk opens ERPNext's
 * own URL, which the Administration screen's Consoles tab prints. The role still gates blocks that only they should
 * see — the deployment's consoles among them. */
export const SUPER_ADMIN_ROLES: readonly string[] = ["os_admin"];

export function isSuperAdmin(roles: readonly string[]): boolean {
  return roles.some((r) => SUPER_ADMIN_ROLES.includes(r));
}

/** os_* realm roles from an access-token payload (realm_access.roles) — mirrors ROLE_MAP in onesphere/auth.py — plus the
 * of_* roles of the OneFleet add-on, which gate the /fleet module in the same way (OneFleet AD-03). */
export function rolesFromPayload(payload: Record<string, unknown>): string[] {
  const realm = payload.realm_access as { roles?: unknown } | undefined;
  const roles = Array.isArray(realm?.roles) ? (realm!.roles as unknown[]) : [];
  // OneSphere roles first, then the add-on's, each sorted — the user menu reads them in that order
  const os = (r: string) => r.startsWith("os_");
  return roles
    .filter((r): r is string => typeof r === "string" && (os(r) || isOneFleetRole(r)))
    .sort((a, b) => (os(a) === os(b) ? a.localeCompare(b) : os(a) ? -1 : 1));
}

export function identityFromUser(user: User): Identity {
  const payload = decodeJwtPayload(user.access_token);
  const p = user.profile;
  return {
    sub: p.sub,
    username: (p.preferred_username as string | undefined) ?? p.sub,
    name: (p.name as string | undefined) ?? (p.preferred_username as string | undefined) ?? p.sub,
    email: p.email,
    roles: rolesFromPayload(payload),
    expiresAt: user.expires_at ?? 0,
  };
}

let manager: UserManager | null = null;

export function userManager(): UserManager {
  if (!manager) {
    manager = new UserManager({
      authority: AUTHORITY,
      client_id: KEYCLOAK_CLIENT_ID,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: window.location.origin,
      response_type: "code",
      scope: "openid profile email",
      automaticSilentRenew: true,
      includeIdTokenInSilentRenew: false,
      monitorSession: false,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });
  }
  return manager;
}

/** Human-readable label for an os_* role, used on the landing page (i18n keys roles.<role>). */
export const ROLE_LABEL_KEYS: Record<string, string> = {
  os_executive: "roles.executive",
  os_plant_gm: "roles.plantGm",
  os_finance: "roles.finance",
  os_hr: "roles.hr",
  os_project_manager: "roles.projectManager",
  os_procurement: "roles.procurement",
  os_maintenance: "roles.maintenance",
  os_operator: "roles.operator",
  os_store_manager: "roles.storeManager",
  os_storekeeper: "roles.storekeeper",
  os_security: "roles.security",
  os_cashier: "roles.cashier",
  os_sales: "roles.sales",
  os_admin: "roles.admin",
  ...OF_ROLE_LABEL_KEYS,
};
